import type { CurrentUser, PermissionAction, PermissionModule } from '@/api/schema';

/**
 * How a matrix arrives over the wire.
 *
 * Partial on purpose: the generated type promises every module, but a response is
 * data, not a guarantee. Treating a missing module as denied is the safe direction.
 */
type WireMatrix = Partial<Record<PermissionModule, Partial<Record<PermissionAction, boolean>>>>;

/**
 * Can this user do this?
 *
 * The same dictionary lookup the server does, for the same reason: no inheritance, no
 * wildcards, no implication between actions. A role that can `edit` cannot `view`
 * unless it was granted `view`.
 *
 * **This is cosmetic.** It decides what to show. The server checks again on every
 * request and that check is the real one; hiding a button has never stopped anyone
 * calling an endpoint.
 */
export function can(
  user: Pick<CurrentUser, 'permissions'> | null | undefined,
  module: PermissionModule,
  action: PermissionAction = 'view',
): boolean {
  if (!user) return false;
  const matrix = user.permissions as WireMatrix;
  return matrix[module]?.[action] === true;
}

/** True when the user can see nothing at all (a role with every box unticked). */
export function hasNoAccess(
  user: Pick<CurrentUser, 'permissions'> | null | undefined,
): boolean {
  if (!user) return true;
  const matrix = user.permissions as WireMatrix;
  return !Object.values(matrix).some((actions) => actions.view === true);
}
